<template>
  <div class="agent-chat-container">
    <div class="glass-card" :class="{ 'fullscreen-mode': isFullscreen }">
      <!-- Header -->
      <div class="chat-header">
        <div class="header-left">
          <div class="logo-wrapper">
            <img :src="logoUrl" alt="智能助手" class="header-logo" />
          </div>
          <div class="header-info">
            <span class="title">智能助手</span>
            <span class="subtitle">Smart Assistant</span>
          </div>
        </div>
        <div class="header-right">
          <div class="status-dot"></div>
          <span class="status-text">在线</span>
          <div class="fullscreen-btn" @click="toggleFullscreen" :title="isFullscreen ? '退出全屏' : '全屏'">
            <a-icon :type="isFullscreen ? 'fullscreen-exit' : 'fullscreen'" />
          </div>
          <div class="close-btn" @click="$emit('close')">
            <a-icon type="close" />
          </div>
        </div>
      </div>
      
      <!-- Messages Area -->
      <div class="chat-messages" ref="messagesContainer">
        <div v-if="messages.length === 0" class="empty-state">
          <div class="empty-icon-wrapper">
            <img :src="logoUrl" alt="智能助手" class="empty-logo" />
            <div class="pulse-ring"></div>
          </div>
          <h3 class="empty-title">我是您的智能助手</h3>
          <p class="empty-text">我可以帮您查询数据、解答问题或提供操作指南</p>
          <div class="suggestion-chips">
            <div class="chip" @click="quickAsk('如何使用系统？')">如何使用系统？</div>
            <div class="chip" @click="quickAsk('系统有哪些功能？')">系统有哪些功能？</div>
          </div>
        </div>
        
        <div
          v-for="(message, index) in messages"
          :key="index"
          :class="['message-item', message.type]"
        >
          <div class="message-avatar" :class="message.type">
            <a-icon v-if="message.type === 'user'" type="user" />
            <img v-else :src="logoUrl" alt="智能助手" class="assistant-logo" />
          </div>
          <div class="message-content-wrapper">
            <div class="message-name">{{ message.type === 'user' ? '您' : '智能助手' }} <span class="time">{{ message.time }}</span></div>
            <div class="message-content" :class="message.type">
              <!-- 如果是最后一条助手消息且正在加载且文本为空，显示加载指示器 -->
              <template v-if="loading && message.type === 'assistant' && index === messages.length - 1 && !message.text">
                <div class="typing-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <span class="typing-text">正在思考...</span>
              </template>
              <!-- 否则显示消息内容 -->
              <template v-else>
                <!-- 图片显示功能暂时隐藏 -->
                <!-- <div v-if="message.image || message.imageUrl" class="message-image">
                  <img :src="message.imageUrl || message.image" alt="上传的图片" @click="previewImage(message.imageUrl || message.image)" />
                </div> -->
                <div class="message-text" v-html="formatMessage(message.text)"></div>
              </template>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Input Area -->
      <div class="chat-input-area">
        <div class="input-wrapper">
          <a-textarea
            v-model="userInput"
            placeholder="请输入您的问题..."
            :auto-size="{ minRows: 1, maxRows: 4 }"
            @pressEnter="handleSendWithShift"
            :disabled="loading"
            class="chat-textarea"
          />
          <div class="input-actions">
            <!-- 图片上传输入框暂时隐藏 -->
            <!-- <input
              ref="imageInput"
              type="file"
              accept="image/*"
              style="display: none"
              @change="handleImageSelect"
            /> -->
            <!-- 图片上传功能暂时隐藏 -->
            <!-- <a-tooltip :title="uploadingImage ? '图片上传中...' : '上传图片'">
              <span>
                <button 
                  class="image-btn"
                  :class="{ 'uploading': uploadingImage }"
                  @click="triggerImageSelect"
                  :disabled="loading || uploadingImage"
                >
                  <a-icon :type="uploadingImage ? 'loading' : 'picture'" :spin="uploadingImage" />
                </button>
              </span>
            </a-tooltip> -->
            <a-tooltip :title="voiceButtonTooltip">
              <span>
                <button 
                  class="voice-btn"
                  :class="{ 'recording': isRecording, 'disabled': !isSpeechSupported }"
                  @click="toggleRecording"
                  :disabled="loading || !isSpeechSupported"
                >
                  <a-icon :type="isRecording ? 'audio' : 'audio'" />
                </button>
              </span>
            </a-tooltip>
            <a-tooltip title="发送 (Enter)">
              <span>
                <button 
                  class="send-btn"
                  @click="handleSend"
                  :disabled="!userInput.trim() || !appId || loading || uploadingImage"
                >
                  <a-icon type="arrow-up" />
                </button>
              </span>
            </a-tooltip>
          </div>
        </div>
        <div class="input-footer">
          <span class="footer-tip">Enter 发送，Shift + Enter 换行</span>
          <span v-if="isRecording" class="recording-tip">
            <a-icon type="audio" /> 正在录音中... {{ recordingTime }}s
          </span>
          <span v-if="speechText" class="speech-text-preview">
            <a-icon type="sound" /> {{ speechText }}
          </span>
          <!-- 图片预览暂时隐藏 -->
          <!-- <div v-if="selectedImage" class="selected-image-preview">
            <img :src="selectedImage" alt="选择的图片" />
            <a-icon v-if="!uploadingImage" type="close-circle" @click="clearSelectedImage" class="remove-image-btn" />
            <a-icon v-if="uploadingImage" type="loading" class="uploading-icon" />
          </div> -->
          <span v-if="!appId" class="error-tip">
            <a-icon type="exclamation-circle" /> 请配置 APP_ID
          </span>
        </div>
      </div>
    </div>
    
    <!-- 语音识别错误提示弹窗 -->
    <a-modal
      title="语音识别错误"
      :visible="errorModalVisible"
      :maskClosable="true"
      :closable="true"
      @cancel="closeErrorModal"
      @ok="closeErrorModal"
      :width="500"
      :z-index="10003"
      :get-container="getModalContainer"
      wrap-class-name="speech-error-modal"
    >
      <div class="error-modal-content">
        <a-icon type="close-circle" :style="{ color: '#ff4d4f', fontSize: '24px', marginRight: '12px' }" />
        <div class="error-text">
          <p style="font-size: 16px; font-weight: 500; margin-bottom: 8px;">{{ errorTitle }}</p>
          <p style="color: #666; font-size: 14px; line-height: 1.6;">{{ errorMessage }}</p>
        </div>
      </div>
      <template slot="footer">
        <a-button type="primary" @click="closeErrorModal">确定</a-button>
      </template>
    </a-modal>
    
    <!-- 图片预览弹窗 -->
    <a-modal
      title="图片预览"
      :visible="imagePreviewVisible"
      :footer="null"
      :maskClosable="true"
      :closable="true"
      @cancel="closeImagePreview"
      :width="800"
      :z-index="10004"
      :get-container="getModalContainer"
      wrap-class-name="image-preview-modal"
    >
      <div class="image-preview-content">
        <img :src="previewImageUrl" alt="预览图片" />
      </div>
    </a-modal>
  </div>
</template>

<script>
import { callAgent ,callAgentStream} from '@/api/agent'
import moment from 'moment'
import logoUrl from '@/assets/myImg/1/合汇LOGO-渐变.svg'
import MarkdownIt from 'markdown-it'
import mk from '@traptitech/markdown-it-katex'
import 'katex/dist/katex.min.css'
import SpeechRecognitionService from '@/utils/speechRecognition'
import request from '@/utils/request'

export default {
  name: 'AgentChat',
  props: {
    appId: {
      type: String,
      default: () => localStorage.getItem('AGENT_APP_ID') || ''
    }
  },
  data() {
    return {
      messages: [],
      userInput: '',
      loading: false,
      logoUrl: logoUrl,
      md: null,
      // 语音识别相关
      speechService: null,
      isRecording: false,
      speechText: '',
      recordingTime: 0,
      recordingTimer: null,
      isStopping: false, // 标记是否正在停止录音，防止重复调用
      // 错误提示弹窗
      errorModalVisible: false,
      errorTitle: '',
      errorMessage: '',
      // 图片相关
      selectedImage: null, // 当前选择的图片预览URL（base64，用于预览显示）
      selectedImageUrl: null, // 当前选择的图片公网URL（用于API调用）
      imagePreviewVisible: false, // 图片预览弹窗
      previewImageUrl: '', // 预览的图片URL
      uploadingImage: false, // 图片上传中状态
      isFullscreen: false // 全屏状态
    }
  },
  computed: {
    // 检查是否支持语音功能（需要在安全上下文中）
    isSpeechSupported() {
      // 检查浏览器是否支持 getUserMedia
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        return false
      }
      
      // 检查是否在安全上下文中（HTTPS 或 localhost）
      const isSecureContext = window.isSecureContext || 
                              location.protocol === 'https:' || 
                              location.hostname === 'localhost' || 
                              location.hostname === '127.0.0.1' ||
                              location.hostname === '[::1]'
      
      return isSecureContext
    },
    // 获取语音按钮的提示信息
    voiceButtonTooltip() {
      if (this.isRecording) {
        return '点击停止录音'
      }
      if (!this.isSpeechSupported) {
        return '语音功能需要在 HTTPS 环境下使用'
      }
      return '点击开始语音输入'
    }
  },
  mounted() {
    // 配置 MarkdownIt
    this.md = new MarkdownIt({
      html: true,
      breaks: true,
      linkify: true
    })

    // 配置 KaTeX 插件（使用 @traptitech/markdown-it-katex）
    this.md.use(mk, {
      throwOnError: false,
      errorColor: '#cc0000'
    })

    // 初始化语音识别服务
    this.initSpeechService()

    // this.addWelcomeMessage()
  },
  beforeDestroy() {
    // 组件销毁前停止录音，不显示提示消息
    if (this.isRecording) {
      this.stopRecording(false)
    }
    if (this.recordingTimer) {
      clearInterval(this.recordingTimer)
    }
  },
  methods: {
    addWelcomeMessage() {
      this.messages.push({
        type: 'assistant',
        text: '您好！我是**合汇科技智能助手**。我可以帮助您：\n\n- 📚 了解系统功能特点\n- 🔍 查询操作指南\n- ❓ 解答常见问题\n\n请告诉我您需要什么帮助？',
        time: moment().format('HH:mm')
      })
    },
    quickAsk(text) {
      this.userInput = text
      this.handleSend()
    },
    handleSendWithShift(e) {
      if (e.shiftKey) return
      e.preventDefault()
      this.handleSend()
    },
    async handleSend() {
      // 必须有文字描述才能发送
      if (!this.userInput.trim()) {
        // 如果有图片，提示需要文字描述
        if (this.selectedImageUrl) {
          this.$message.warning('发送图片时必须包含文字描述')
        } else {
          this.$message.warning('请输入问题描述')
        }
        return
      }
      
      // 如果图片还在上传中，等待上传完成
      if (this.uploadingImage) {
        this.$message.warning('图片正在上传中，请稍候...')
        return
      }
      
      if (!this.appId) {
        this.$message.error('请先配置应用ID (APP_ID)')
        return
      }
      
      const userMessage = {
        type: 'user',
        text: this.userInput.trim() || '',
        image: this.selectedImage || null, // 用于显示，使用base64预览
        imageUrl: this.selectedImageUrl || null, // 保存原始URL用于预览
        time: moment().format('HH:mm')
      }
      this.messages.push(userMessage)
      
      const question = this.userInput.trim()
      const imageUrl = this.selectedImageUrl
      
      // 清空输入和图片
      this.userInput = ''
      this.selectedImage = null
      this.selectedImageUrl = null
      this.loading = true
      
      this.$nextTick(() => this.scrollToBottom())
      
      // 创建助手消息对象，用于流式更新
      const assistantMessage = {
        type: 'assistant',
        text: '',
        time: moment().format('HH:mm')
      }
      this.messages.push(assistantMessage)
      const assistantIndex = this.messages.length - 1
      
      // 标记是否已收到第一个文本片段
      let hasReceivedFirstChunk = false
      
      try {
        // 准备图片列表（根据阿里云文档，image_list 是图片公网 URL 数组）
        const imageList = imageUrl ? [imageUrl] : []
        
        // 使用流式调用，传入图片列表
        const result = await callAgentStream(
          this.appId,
          question || '', // prompt（如果没有文本可以为空字符串）
          (text) => {
            // 流式接收消息的回调函数
            if (text) {
              // 追加接收到的文本片段
              // 使用 $set 确保 Vue 2 能够检测到变化并更新视图
              const currentMessage = this.messages[assistantIndex]
              const newText = (currentMessage.text || '') + text
              this.$set(this.messages, assistantIndex, {
                ...currentMessage,
                text: newText
              })
              
              // 收到第一个文本片段时，关闭loading状态
              if (!hasReceivedFirstChunk) {
                hasReceivedFirstChunk = true
                this.loading = false
              }
              
              // 强制更新视图
              this.$forceUpdate()
              
              // 实时滚动到底部
              this.$nextTick(() => this.scrollToBottom())
            }
          },
          {
            // 传入图片列表参数（参考：https://help.aliyun.com/zh/model-studio/call-single-agent-application/#4f5e6ac27cakp）
            imageList: imageList
          }
        )
        
        if (result.success) {
          // 如果流式输出后文本仍为空，设置默认提示
          if (!this.messages[assistantIndex].text.trim()) {
            this.messages[assistantIndex].text = result.text || '抱歉，没有收到回复'
          }
          // 确保loading状态已关闭
          if (this.loading) {
            this.loading = false
          }
        } else {
          throw new Error(result.message || '调用失败')
        }
      } catch (error) {
        this.$message.error(error.message || '调用智能体失败')
        // 更新助手消息为错误信息
        if (this.messages[assistantIndex]) {
          this.messages[assistantIndex].text = `抱歉，出现了错误：${error.message || '未知错误'}`
        }
      } finally {
        // 确保loading状态被关闭
        this.loading = false
        this.$nextTick(() => this.scrollToBottom())
      }
    },
    formatMessage(text) {
      if (!text) return ''
      try {
        // 0. 保护 Markdown 图片和链接，防止 URL 中的特殊字符（如 %、=）被误判为 LaTeX 公式
        const linkPlaceholders = []
        let normalizedText = text.replace(/!{0,1}\[[^\]]*\]\([^)]+\)/g, (match) => {
          const index = linkPlaceholders.length
          linkPlaceholders.push(match)
          return `___LINK_MARKER_${index}___`
        })

        // 1. 预处理：只清理明显错误的文档标记（保留正确的Markdown语法）
        normalizedText = normalizedText
          // 清理非行首的孤立 ### （但保留 ### 开头的标题）
          .replace(/([^\n])\s+###\s+([^\n])/g, '$1 $2')
          // 清理 --- ### 这种明显错误的组合
          .replace(/---\s*###\s*([^\n])/g, '---\n\n### $1')

        // 2. 预处理LaTeX：标准化LaTeX标记
        // 先处理块级公式 \[...\] 转换为 $$...$$
        // 保持内部换行格式，支持多行（包括 \[ 和 \] 在单独行的情况）
        // 匹配方式：\\\[ 匹配 \[，[\s\S]*? 非贪婪匹配中间所有内容（包括换行），\\\] 匹配 \]
        normalizedText = normalizedText.replace(/\\\[([\s\S]*?)\\\]/g, (match, formula) => {
          // 清理公式内容的首尾空白行，但保留内部换行
          let cleanedFormula = formula
            .replace(/^\s*\n+/, '')  // 移除开头的空行和空白
            .replace(/\n+\s*$/, '')  // 移除结尾的空行和空白
            .trim()
          
          // 转换为标准 $$...$$ 格式
          // 如果公式是多行的，保持多行格式
          return `$$${cleanedFormula}$$`
        })

        // 行内公式：\(...\) 替换为 $...$
        normalizedText = normalizedText.replace(/\\\(([^\)]*?)\\\)/g, (match, formula) => {
          return `$${formula}$`
        })

        // 2.5. 保护块级公式（$$ ... $$）- 支持多行格式
        const blockFormulas = []
        // 先处理标准的 $$...$$ 格式（支持多行）
        // 使用正则匹配多行块级公式，[\s\S] 匹配包括换行在内的所有字符
        // 使用非贪婪匹配 *? 确保匹配到最近的 $$
        normalizedText = normalizedText.replace(/\$\$([\s\S]*?)\$\$/g, (match, formula) => {
          // 确保匹配的是完整的公式块（包括多行）
          const index = blockFormulas.length
          blockFormulas.push(match) // 保存完整的 $$...$$ 格式（包括换行）
          return `___BLOCK_FORMULA_${index}___`
        })

        // 2.6. 保护行内公式（$ ... $）- 只保护有效的公式
        const inlineFormulas = []
        // 只匹配包含LaTeX命令或数学符号的行内公式
        normalizedText = normalizedText.replace(/\$([^\$\n]{1,200}?)\$/g, (match, formula) => {
          const trimmed = formula.trim()
          // 检查是否包含LaTeX命令或数学符号
          const isFormula = /\\[a-zA-Z]+/.test(trimmed) ||
                           /[_{}\^]/.test(trimmed) ||
                           /[=+\-×÷≈≠≤≥∑∫√]/.test(trimmed)

          if (isFormula) {
            const index = inlineFormulas.length
            inlineFormulas.push(match)
            return `___INLINE_FORMULA_${index}___`
          }
          // 不是公式，移除 $ 符号
          return formula
        })

        // 3. 自动检测并包裹未包裹的 LaTeX 公式（在清理 $ 之前）
        const lines = normalizedText.split('\n')
        const processedLines = lines.map(line => {
          const trimmed = line.trim()

          // 跳过以下情况：
          // - 空行
          // - Markdown语法（标题、列表、引用、代码、分隔线）
          // - 已包裹的公式或已保护的公式占位符
          // 分隔线检测：---、***、___（至少3个连续的相同字符，可能有空格）
          const isHorizontalRule = /^[-*_]{3,}\s*$/.test(trimmed)
          
          if (!trimmed ||
              trimmed.startsWith('#') ||
              trimmed.match(/^[-*+]\s/) ||
              trimmed.match(/^\d+\.\s/) ||
              trimmed.startsWith('> ') ||
              trimmed.startsWith('```') ||
              isHorizontalRule ||
              trimmed.includes('$$') ||
              trimmed.includes('___BLOCK_FORMULA_') ||
              trimmed.includes('___INLINE_FORMULA_') ||
              (trimmed.includes('$') && trimmed.split('$').length > 2) ||
              trimmed.startsWith('`')) {
            return line
          }

          // 检测LaTeX命令（包含 \text, \frac, \times, \% 等）
          const hasLatexCommands = /\\[a-zA-Z]+\{/.test(trimmed) ||
                                   /\\[a-zA-Z]+\s/.test(trimmed) ||
                                   /\\%/.test(trimmed) || // 检测 LaTeX 百分号 \%
                                   /\\(?:frac|text|times|sqrt|sum|int|left|right|cdot|sin|cos|log|ln|exp|alpha|beta|gamma|delta|pi|sigma|theta|lambda|mu|nu|omega)/i.test(trimmed) ||
                                   /\\[a-zA-Z]+/.test(trimmed) // 匹配任何 LaTeX 命令

          // 检测数学符号和结构
          const hasMathSymbols = /[=+\-×÷≈≠≤≥±∞]/.test(trimmed) ||
                                 /\d+\s*%/.test(trimmed) ||
                                 /\\%/.test(trimmed) || // 检测 LaTeX 百分号
                                 /[{}()]/.test(trimmed) // 包含大括号或小括号（可能是分数、函数等）

          // 如果包含LaTeX命令或数学符号，包裹为公式
          // 如果只包含简单的数学符号且没有LaTeX命令，可能是普通文本
          if (hasLatexCommands || (hasMathSymbols && /[{}()]/.test(trimmed))) {
            // 检查是否是行内公式（简短且包含常见行内结构）
            // 增加长度限制到150字符，因为有些公式可能稍长
            const isShortFormula = trimmed.length < 150 && 
                                  (trimmed.includes('=') || trimmed.includes('\\text') || trimmed.includes('\\frac') || hasLatexCommands)
            
            // 如果公式包含 \frac（分数），通常是块级公式，除非非常短
            const hasComplexStructure = trimmed.includes('\\frac') || trimmed.includes('\\times') || trimmed.includes('\\%')
            
            if (isShortFormula && !trimmed.includes('\n') && !hasComplexStructure) {
              // 简单的行内公式
              return `$${trimmed}$`
            } else {
              // 块级公式（复杂结构或较长的公式）
              return `\n$$${trimmed}$$\n`
            }
          }

          return line
        })

        normalizedText = processedLines.join('\n')

        // 2.7. 重新保护所有公式（包括新检测到的）
        // 先保护块级公式
        normalizedText = normalizedText.replace(/\$\$([\s\S]*?)\$\$/g, (match, formula) => {
          // 检查是否已经在保护列表中
          const existingIndex = blockFormulas.findIndex(f => f === match)
          if (existingIndex >= 0) {
            return `___BLOCK_FORMULA_${existingIndex}___`
          }
          const index = blockFormulas.length
          blockFormulas.push(match)
          return `___BLOCK_FORMULA_${index}___`
        })

        // 再保护行内公式（包括新检测到的）
        normalizedText = normalizedText.replace(/\$([^\$\n]{1,200}?)\$/g, (match, formula) => {
          const trimmed = formula.trim()
          // 检查是否包含LaTeX命令或数学符号
          const isFormula = /\\[a-zA-Z]+/.test(trimmed) ||
                           /[_{}\^]/.test(trimmed) ||
                           /[=+\-×÷≈≠≤≥∑∫√]/.test(trimmed)

          if (isFormula) {
            // 检查是否已经在保护列表中
            const existingIndex = inlineFormulas.findIndex(f => f === match)
            if (existingIndex >= 0) {
              return `___INLINE_FORMULA_${existingIndex}___`
            }
            const index = inlineFormulas.length
            inlineFormulas.push(match)
            return `___INLINE_FORMULA_${index}___`
          }
          // 不是公式，移除 $ 符号
          return formula
        })

        // 2.8. 清理剩余的孤立 $ 符号
        normalizedText = normalizedText.replace(/\$/g, '')

        // 5. 确保块级公式独占一行（已在恢复时处理，这里不需要了）
        // 块级公式已被保护为占位符，恢复时会自动处理

        // 6. 优化Markdown元素前的空行
        // 列表前需要空行（但不是嵌套列表）
        normalizedText = normalizedText.replace(/([^\n])\n([-*]\s|\d+\.\s)/g, (match, before, listMarker) => {
          // 检查前一行是否是列表项或空行
          const beforeText = normalizedText.substring(0, normalizedText.indexOf(match))
          const previousLine = beforeText.split('\n').pop() || ''
          const trimmedPrev = previousLine.trim()

          // 如果前一行是列表项、空行或以空格开头（缩进的嵌套列表），不添加额外空行
          if (!trimmedPrev ||
              trimmedPrev.match(/^[-*]\s/) ||
              trimmedPrev.match(/^\d+\.\s/) ||
              previousLine.startsWith('   ')) {
            return match
          }
          return `${before}\n\n${listMarker}`
        })

        // 引用块前需要空行
        normalizedText = normalizedText.replace(/([^\n])\n(>\s)/g, '$1\n\n$2')

        // 标题前需要空行
        normalizedText = normalizedText.replace(/([^\n])\n(#{1,6}\s)/g, '$1\n\n$2')

        // 分隔线前后需要空行（支持 ---、***、___ 等格式）
        // 确保分隔线前后都有空行，以便 Markdown 正确识别为分隔线
        normalizedText = normalizedText.replace(/([^\n])\n([-*_]{3,}\s*$)/gm, '$1\n\n$2')
        normalizedText = normalizedText.replace(/(^[-*_]{3,}\s*)\n([^\n])/gm, '$1\n\n$2')
        
        // 确保分隔线格式标准（至少3个相同字符，可以有空格）
        // 这个处理确保分隔线能被 markdown-it 正确识别
        normalizedText = normalizedText.replace(/^([-*_])\1{2,}\s*$/gm, (match) => {
          // 标准分隔线格式：至少3个相同字符，可以有尾随空格
          return match.trim() || match // 如果 trim 后为空则保持原样（不应该发生）
        })

        // 7. 优化特定格式
        // 列表项中的加粗文本后跟冒号
        normalizedText = normalizedText.replace(/(\d+\.)\s*\*\*(.+?)\*\*[:：]\s*/g, '$1 **$2**：')

        // 引用块中的"注："加粗
        normalizedText = normalizedText.replace(/>\s*注[:：]\s*/g, '> **注：** ')

        // 引用块中的"操作路径："加粗
        normalizedText = normalizedText.replace(/>\s*操作路径[:：]\s*/g, '> **操作路径：** ')

        // 优化箭头符号的显示（保持原样，不转换）
        // Markdown会自动处理 → 符号

        // 8. 清理多余空行（保留最多两个换行符，即一个空行）
        normalizedText = normalizedText.replace(/\n{3,}/g, '\n\n')

        // 9. 最终确保分隔线格式正确（在清理首尾空白之前）
        // 确保分隔线前后都有空行，且分隔线格式标准
        normalizedText = normalizedText.replace(/([^\n])\n([-*_]{3,}\s*)\n([^\n])/g, '$1\n\n$2\n\n$3')
        normalizedText = normalizedText.replace(/([^\n])\n([-*_]{3,}\s*)$/gm, '$1\n\n$2')
        normalizedText = normalizedText.replace(/^([-*_]{3,}\s*)\n([^\n])/gm, '$1\n\n$2')

        // 10. 清理首尾空白
        normalizedText = normalizedText.trim()

        // 11. 恢复块级公式（转换为 markdown-it-katex 支持的格式）
        normalizedText = normalizedText.replace(/___BLOCK_FORMULA_(\d+)___/g, (match, index) => {
          let formula = blockFormulas[parseInt(index)] || match

          // 如果公式已经是完整的 $$...$$ 格式（可能包含换行）
          if (formula.startsWith('$$') && formula.endsWith('$$')) {
            // 提取公式内容（去除首尾的 $$ 标记）
            let formulaContent = formula.substring(2, formula.length - 2).trim()

            // 检查公式内容是否为空
            if (!formulaContent) {
              return ''
            }

            // 将多行公式转换为单行
            formulaContent = formulaContent
              .replace(/\n+/g, ' ')        // 将换行替换为空格
              .replace(/\s+/g, ' ')         // 合并多个空格为一个
              .trim()

            // **关键修复**：使用 markdown-it-katex 要求的格式
            // $$ 必须在单独的行上
            const result = `\n\n$$\n${formulaContent}\n$$\n\n`
            return result
          }

          // 如果格式不对，尝试修复
          return `\n\n$$\n${formula}\n$$\n\n`
        })

        // 11.5. 清理公式恢复后可能产生的多余空行
        normalizedText = normalizedText.replace(/\n{4,}/g, '\n\n')

        // 12. 恢复行内公式
        normalizedText = normalizedText.replace(/___INLINE_FORMULA_(\d+)___/g, (match, index) => {
          return inlineFormulas[parseInt(index)] || match
        })

        // 12.5 恢复 Markdown 图片和链接（在渲染前恢复，以便 markdown-it 能正确解析）
        normalizedText = normalizedText.replace(/___LINK_MARKER_(\d+)___/g, (match, index) => {
          return linkPlaceholders[parseInt(index)] || match
        })

        // 13. 使用markdown-it渲染
        const rendered = this.md.render(normalizedText)

        return rendered
      } catch (e) {
        return text.replace(/\n/g, '<br>')
      }
    },
    scrollToBottom() {
      const container = this.$refs.messagesContainer
      if (container) {
        container.scrollTop = container.scrollHeight
      }
    },
    /**
     * 初始化语音识别服务
     */
    initSpeechService() {
      this.speechService = new SpeechRecognitionService()
      
      // 设置回调函数
      this.speechService.setCallbacks({
        // 识别结果回调
        onResult: (text) => {
          // 实时更新识别文本
          this.speechText = text
          // 同时更新输入框内容
          this.userInput = text
        },
        // 错误回调
        onError: (error) => {
          this.showErrorModal('语音识别失败', error.message || '未知错误')
          // 错误时停止录音，不显示提示消息（因为已经显示了错误弹窗）
          this.stopRecording(false)
        },
        // 状态变化回调
        onStatusChange: (status) => {
          if (status === 'stopped' || status === 'completed') {
            // 状态变化时停止录音，但不显示提示消息（避免重复提示）
            // 提示消息由用户手动点击停止按钮时显示
            this.stopRecording(false)
          }
        }
      })
    },
    /**
     * 切换录音状态
     */
    async toggleRecording() {
      if (this.isRecording) {
        await this.stopRecording()
      } else {
        await this.startRecording()
      }
    },
    /**
     * 开始录音
     */
    async startRecording() {
      try {
        // 检查浏览器支持
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          this.showErrorModal(
            '浏览器不支持',
            '您的浏览器不支持语音输入功能，请使用 Chrome、Firefox 或 Edge 等现代浏览器。'
          )
          return
        }

        // 检查协议要求：getUserMedia 需要在安全上下文中使用（HTTPS 或 localhost）
        const isSecureContext = window.isSecureContext || 
                                location.protocol === 'https:' || 
                                location.hostname === 'localhost' || 
                                location.hostname === '127.0.0.1' ||
                                location.hostname === '[::1]'
        
        if (!isSecureContext) {
          const currentUrl = location.protocol + '//' + location.hostname + (location.port ? ':' + location.port : '')
          this.showErrorModal(
            '需要 HTTPS 环境',
            '语音输入功能需要在 HTTPS 环境下使用，或者使用 localhost 访问。\n\n' +
            '当前访问地址：' + currentUrl + '\n' +
            '当前协议：' + location.protocol + '\n\n' +
            '解决方案：\n' +
            '1. 请联系管理员为测试环境配置 HTTPS 证书（推荐使用 Let\'s Encrypt 免费证书）\n' +
            '2. 或者使用 localhost 进行本地测试\n' +
            '3. 或者使用自签名证书（仅测试环境，浏览器会显示安全警告）'
          )
          return
        }

        // 重置状态
        this.speechText = ''
        this.recordingTime = 0
        this.isRecording = true

        // 开始录音计时
        this.recordingTimer = setInterval(() => {
          this.recordingTime++
        }, 1000)

        // 启动语音识别服务
        await this.speechService.start()
        
        this.$message.success('开始录音，请说话...')
      } catch (error) {
        // 根据错误类型显示不同的错误信息
        let errorTitle = '启动录音失败'
        let errorMessage = error.message || '未知错误'
        
        // 判断错误类型
        if (errorMessage.includes('Token') || errorMessage.includes('token')) {
          errorTitle = '连接失败'
          errorMessage = '无法获取语音识别服务Token，请检查网络连接或联系管理员。\n\n错误详情：' + errorMessage
        } else if (errorMessage.includes('WebSocket') || errorMessage.includes('连接')) {
          errorTitle = 'WebSocket连接失败'
          errorMessage = '无法连接到语音识别服务器，请检查：\n1. 网络连接是否正常\n2. 服务配置是否正确\n\n错误详情：' + errorMessage
        } else if (errorMessage.includes('权限') || errorMessage.includes('Permission') || errorMessage.includes('NotAllowedError')) {
          errorTitle = '麦克风权限被拒绝'
          errorMessage = '无法访问麦克风，请：\n1. 允许浏览器访问麦克风权限\n2. 检查系统麦克风设置\n\n错误详情：' + errorMessage
        } else if (errorMessage.includes('NotSupportedError') || errorMessage.includes('NotReadableError') || errorMessage.includes('getUserMedia')) {
          // 可能是协议问题导致的错误
          const isSecureContext = window.isSecureContext || 
                                  location.protocol === 'https:' || 
                                  location.hostname === 'localhost' || 
                                  location.hostname === '127.0.0.1'
          
          if (!isSecureContext) {
            errorTitle = '需要 HTTPS 环境'
            errorMessage = '语音输入功能需要在 HTTPS 环境下使用。\n\n' +
                          '当前访问协议：' + location.protocol + '\n' +
                          '当前域名：' + location.hostname + '\n\n' +
                          '解决方案：\n' +
                          '1. 请联系管理员为测试环境配置 HTTPS 证书\n' +
                          '2. 或者使用 localhost 进行本地测试\n\n' +
                          '错误详情：' + errorMessage
          } else {
            errorTitle = '浏览器不支持'
            errorMessage = '您的浏览器可能不支持语音输入功能，请使用 Chrome、Firefox 或 Edge 等现代浏览器。\n\n错误详情：' + errorMessage
          }
        }
        
        this.showErrorModal(errorTitle, errorMessage)
        // 错误时停止录音，不显示提示消息（因为已经显示了错误弹窗）
        this.stopRecording(false)
      }
    },
    /**
     * 停止录音
     */
    async stopRecording(showMessage = true) {
      // 如果正在停止，直接返回，避免重复调用
      if (this.isStopping) {
        return
      }

      try {
        this.isStopping = true
        this.isRecording = false
        
        // 停止计时器
        if (this.recordingTimer) {
          clearInterval(this.recordingTimer)
          this.recordingTimer = null
        }

        // 停止语音识别服务
        if (this.speechService) {
          await this.speechService.stop()
        }

        // 如果需要显示提示消息（防止重复提示）
        if (showMessage) {
          // 如果有识别结果，提示用户
          if (this.speechText) {
            this.$message.success('识别完成')
            // 可以选择自动发送或让用户确认
            // 这里不自动发送，让用户确认后手动发送
          } else {
            this.$message.info('未识别到语音内容')
          }
        }
      } catch (error) {
        this.showErrorModal('停止录音失败', error.message || '未知错误')
      } finally {
        // 重置停止标志
        this.isStopping = false
      }
    },
    /**
     * 显示错误提示弹窗
     */
    showErrorModal(title, message) {
      this.errorTitle = title
      this.errorMessage = message
      this.errorModalVisible = true
    },
    /**
     * 关闭错误提示弹窗
     */
    closeErrorModal() {
      this.errorModalVisible = false
      this.errorTitle = ''
      this.errorMessage = ''
    },
    /**
     * 获取弹窗挂载容器，确保弹窗显示在最前面
     */
    getModalContainer() {
      // 挂载到 body，确保不受父级弹窗影响
      return document.body
    },
    /**
     * 触发图片选择
     */
    triggerImageSelect() {
      this.$refs.imageInput.click()
    },
    /**
     * 处理图片选择并上传
     */
    async handleImageSelect(event) {
      const file = event.target.files[0]
      if (!file) return
      
      // 检查文件类型
      if (!file.type.startsWith('image/')) {
        this.$message.error('请选择图片文件')
        return
      }
      
      // 检查文件大小（限制为5MB）
      const maxSize = 5 * 1024 * 1024 // 5MB
      if (file.size > maxSize) {
        this.$message.error('图片大小不能超过5MB')
        return
      }
      
      // 先读取图片为base64用于预览
      const reader = new FileReader()
      reader.onload = (e) => {
        this.selectedImage = e.target.result
      }
      reader.onerror = () => {
        this.$message.error('图片读取失败')
        return
      }
      reader.readAsDataURL(file)
      
      // 上传图片到服务器获取公网URL
      try {
        this.uploadingImage = true
        const formData = new FormData()
        formData.append('file', file)
        const response = await request.post('api/file/upload-image', formData, {
        
        // const response = await request.post('manager/file/resources/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        
        if (response.code === 0 &&   response.message) {
          // 获取图片URL（可能是相对路径或完整URL）
          let imageUrl = response.message
          
          // 如果是相对路径，需要拼接baseURL
          if (imageUrl && !imageUrl.startsWith('http://') && !imageUrl.startsWith('https://')) {
            const baseURL = process.env.VUE_APP_API_BASE_URL || ''
            // 移除baseURL末尾的斜杠（如果有）
            const base = baseURL.endsWith('/') ? baseURL.slice(0, -1) : baseURL
            // 确保imageUrl以斜杠开头
            const url = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`
            imageUrl = `${base}${url}`
          }
          
          this.selectedImageUrl = imageUrl
          this.$message.success('图片上传成功，可以发送了')
        } else {
          throw new Error(response.message || '上传失败')
        }
      } catch (error) {
        console.error('图片上传失败:', error)
        this.$message.error(error.message || '图片上传失败，请重试')
        // 上传失败，清空选择的图片
        this.selectedImage = null
        this.selectedImageUrl = null
      } finally {
        this.uploadingImage = false
        // 重置input，以便可以重复选择同一文件
        event.target.value = ''
      }
    },
    /**
     * 清空选择的图片
     */
    clearSelectedImage() {
      this.selectedImage = null
      this.selectedImageUrl = null
      this.uploadingImage = false
    },
    /**
     * 预览图片
     */
    previewImage(imageUrl) {
      this.previewImageUrl = imageUrl
      this.imagePreviewVisible = true
    },
    /**
     * 关闭图片预览
     */
    closeImagePreview() {
      this.imagePreviewVisible = false
      this.previewImageUrl = ''
    },
    /**
     * 切换全屏状态
     */
    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen
    }
  }
}
</script>

<style lang="less" scoped>
// 变量定义 - 深水晶蓝主题 (Deep Crystal)
@primary-color: #096dd9;
@primary-gradient: linear-gradient(135deg, #1890ff 0%, #096dd9 100%);
@glass-bg: rgba(230, 245, 255, 0.9);
@glass-border: 1px solid rgba(255, 255, 255, 0.5);
@glass-shadow: 0 12px 48px 0 rgba(24, 144, 255, 0.15);
@text-main: #002766;
@text-secondary: #537895;
@input-bg: rgba(255, 255, 255, 0.6);
@message-assistant-bg: rgba(255, 255, 255, 0.85);

.agent-chat-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  font-family: 'PingFang SC', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  padding: 0;
}

.glass-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: @glass-bg;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 16px;
  border: @glass-border;
  box-shadow: @glass-shadow;
  overflow: hidden;
}

// Header Styles
.chat-header {
  height: 70px;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  background: rgba(255, 255, 255, 0.3);
  
  .header-left {
    display: flex;
    align-items: center;
    gap: 12px;
    
    .logo-wrapper {
      width: 40px;
      height: 40px;
      background: #fff;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
      
      .header-logo {
        width: 24px;
        height: 24px;
      }
    }
    
    .header-info {
      display: flex;
      flex-direction: column;
      
      .title {
        font-size: 16px;
        font-weight: 600;
        color: @text-main;
      }
      
      .subtitle {
        font-size: 12px;
        color: @text-secondary;
        letter-spacing: 0.5px;
      }
    }
  }
  
  .header-right {
    display: flex;
    align-items: center;
    gap: 12px;
    
    .status-dot {
      width: 8px;
      height: 8px;
      background: #52c41a;
      border-radius: 50%;
      box-shadow: 0 0 0 2px rgba(82, 196, 26, 0.2);
    }
    
    .status-text {
      font-size: 12px;
      color: #52c41a;
      font-weight: 500;
      margin-right: 8px;
    }
    
    .fullscreen-btn, .close-btn {
      width: 28px;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      cursor: pointer;
      transition: all 0.3s;
      color: @text-secondary;
      
      &:hover {
        background: rgba(0, 0, 0, 0.05);
        color: @text-main;
      }
    }
  }
}

// Fullscreen Mode Styles
.glass-card.fullscreen-mode {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  z-index: 10005; // Ensure it's above other elements
  border-radius: 0;
  border: none;
  margin: 0;
  
  .chat-messages {
    height: calc(100vh - 70px - 140px); // Adjust based on header and input area heights
  }
}

// Messages Area
.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 24px 28px;
  scroll-behavior: smooth;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.06);
    border-radius: 10px;
    
    &:hover {
      background: rgba(0, 0, 0, 0.1);
    }
  }
}

.empty-state {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: @text-secondary;
  animation: fadeIn 0.8s ease-out;
  
  .empty-icon-wrapper {
    position: relative;
    width: 96px;
    height: 96px;
    margin-bottom: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #fff;
    border-radius: 28px;
    box-shadow: 0 20px 40px -10px rgba(59, 130, 246, 0.2);
    z-index: 1;
    
    .empty-logo {
      width: 48px;
      height: 48px;
      object-fit: contain;
    }
    
    .pulse-ring {
      position: absolute;
      top: -8px;
      left: -8px;
      right: -8px;
      bottom: -8px;
      border-radius: 34px;
      border: 2px solid rgba(59, 130, 246, 0.3);
      opacity: 0;
      animation: pulse-ring 2.5s infinite;
      z-index: -1;
    }
  }
  
  .empty-title {
    font-size: 20px;
    font-weight: 700;
    color: @text-main;
    margin-bottom: 12px;
    letter-spacing: -0.5px;
  }
  
  .empty-text {
    font-size: 15px;
    margin-bottom: 40px;
    opacity: 0.8;
    text-align: center;
    max-width: 280px;
    line-height: 1.6;
  }
  
  .suggestion-chips {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    justify-content: center;
    max-width: 420px;
    
    .chip {
      padding: 10px 20px;
      background: rgba(255, 255, 255, 0.6);
      border: 1px solid rgba(255, 255, 255, 0.8);
      border-radius: 20px;
      cursor: pointer;
      font-size: 13px;
      color: @primary-color;
      font-weight: 500;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
      
      &:hover {
        background: #fff;
        transform: translateY(-2px) scale(1.02);
        box-shadow: 0 10px 15px -3px rgba(59, 130, 246, 0.15);
        color: #2563eb;
      }
      
      &:active {
        transform: scale(0.98);
      }
    }
  }
}

.message-item {
  display: flex;
  margin-bottom: 32px;
  animation: slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  
  &.user {
    flex-direction: row-reverse;
    
    .message-content-wrapper {
      align-items: flex-end;
      
      .message-name {
        text-align: right;
        margin-right: 4px;
      }
    }
    
    .message-avatar {
      margin-left: 16px;
      background: @primary-gradient;
      box-shadow: 0 4px 12px rgba(59, 130, 246, 0.25);
      
      .anticon {
        color: #fff;
        font-size: 20px;
      }
    }
  }
  
  &.assistant {
    .message-content-wrapper {
      align-items: flex-start;
      
      .message-name {
        text-align: left;
        margin-left: 4px;
      }
    }
    
    .message-avatar {
      margin-right: 16px;
      background: #fff;
      padding: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
      
      .assistant-logo {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
    }
  }
  
  .message-avatar {
    width: 44px;
    height: 44px;
    border-radius: 14px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.3s;
    
    &:hover {
      transform: translateY(-2px);
    }
  }
  
  .message-content-wrapper {
    display: flex;
    flex-direction: column;
    max-width: 80%;
    
    .message-name {
      font-size: 12px;
      color: @text-secondary;
      margin-bottom: 8px;
      font-weight: 500;
      
      .time {
        opacity: 0.6;
        margin-left: 6px;
        font-size: 11px;
      }
    }
    
    .message-content {
      padding: 18px 24px;
      border-radius: 18px;
      font-size: 15px;
      line-height: 1.75;
      word-wrap: break-word;
      position: relative;
      transition: all 0.3s;
      
      &.user {
        background: @primary-gradient;
        color: #fff;
        border-top-right-radius: 4px;
        box-shadow: 0 8px 24px -6px rgba(59, 130, 246, 0.4);
      }
      
      &.assistant {
        background: #fff;
        color: @text-main;
        border-top-left-radius: 4px;
        box-shadow: 0 4px 20px -4px rgba(0, 0, 0, 0.05);
        border: 1px solid rgba(0, 0, 0, 0.02);
      }
      
      // Markdown Styles - 优化版
      .message-text {
        line-height: 1.8;
        
        // 段落
        /deep/ p {
          margin: 0 0 14px 0;
          line-height: 1.9;
          color: inherit;
          text-align: justify;
          word-break: break-word;

          &:last-child {
            margin-bottom: 0;
          }

          &:empty {
            display: none;
          }
        }
        
        // 标题样式
        /deep/ h1, /deep/ h2, /deep/ h3, /deep/ h4, /deep/ h5, /deep/ h6 {
          margin: 24px 0 16px 0;
          font-weight: 600;
          line-height: 1.5;
          color: @text-main;
          word-wrap: break-word;

          &:first-child {
            margin-top: 0;
          }
        }

        /deep/ h1 {
          font-size: 1.75em;
          border-bottom: 3px solid @primary-color;
          padding-bottom: 10px;
          margin-bottom: 18px;
        }

        /deep/ h2 {
          font-size: 1.5em;
          border-bottom: 2px solid @primary-color;
          padding-bottom: 8px;
          margin-bottom: 16px;
        }

        /deep/ h3 {
          font-size: 1.35em;
          color: @primary-color;
          padding-left: 12px;
          border-left: 4px solid @primary-color;
          background: linear-gradient(to right, rgba(24, 144, 255, 0.08), transparent);
          padding: 8px 12px;
          border-radius: 4px;
        }

        /deep/ h4 {
          font-size: 1.2em;
          color: @text-main;
          padding-left: 10px;
          border-left: 3px solid @primary-color;
        }

        /deep/ h5, /deep/ h6 {
          font-size: 1.1em;
          color: @text-main;
        }
        
        // 文本格式
        /deep/ strong, /deep/ b {
          font-weight: 600;
          color: @text-main;
          background: linear-gradient(180deg, transparent 65%, rgba(24, 144, 255, 0.15) 65%);
          padding: 0 2px;
        }
        
        /deep/ em, /deep/ i {
          font-style: italic;
          color: @text-secondary;
        }
        
        // 列表样式 - 优化
        /deep/ ul, /deep/ ol {
          margin: 16px 0;
          padding-left: 28px;
          line-height: 2;

          &:first-child {
            margin-top: 0;
          }

          &:last-child {
            margin-bottom: 0;
          }
        }

        /deep/ li {
          margin: 10px 0;
          line-height: 2;
          position: relative;
          padding-left: 4px;

          // 列表项内的段落
          p {
            margin: 8px 0;
            display: inline;

            &:first-child {
              display: inline;
            }
          }

          // 列表项中的加粗文本（如 **进入子系统**）
          strong {
            color: @primary-color;
            font-weight: 600;
          }

          // 列表项中的公式
          .katex-display {
            margin: 14px 0;
          }

          // 嵌套列表的间距
          ul, ol {
            margin-top: 8px;
            margin-bottom: 8px;
            padding-left: 24px;
          }

          // 列表项内的文本换行
          word-wrap: break-word;
          word-break: break-word;
        }

        /deep/ ul {
          list-style-type: disc;

          li::marker {
            color: @primary-color;
            font-size: 1.1em;
          }

          // 二级列表
          ul {
            list-style-type: circle;

            li::marker {
              font-size: 1em;
            }
          }

          // 三级列表
          ul ul {
            list-style-type: square;
          }
        }

        /deep/ ol {
          list-style-type: decimal;

          li::marker {
            color: @primary-color;
            font-weight: 600;
            font-size: 0.95em;
          }

          // 二级列表
          ol {
            list-style-type: lower-alpha;
          }

          // 三级列表
          ol ol {
            list-style-type: lower-roman;
          }
        }
        
        // 引用块 - 优化
        /deep/ blockquote {
          margin: 20px 0;
          padding: 16px 20px 16px 48px;
          border-left: 4px solid @primary-color;
          background: linear-gradient(to right, rgba(24, 144, 255, 0.12), rgba(24, 144, 255, 0.04));
          border-radius: 8px;
          color: @text-main;
          position: relative;
          box-shadow: 0 2px 8px rgba(24, 144, 255, 0.08);

          &::before {
            content: '"';
            position: absolute;
            left: 16px;
            top: 12px;
            font-size: 2.2em;
            color: @primary-color;
            opacity: 0.3;
            font-family: Georgia, serif;
            line-height: 1;
          }

          p {
            margin: 0 0 10px 0;
            position: relative;
            z-index: 1;
            line-height: 1.8;

            &:last-child {
              margin-bottom: 0;
            }
          }

          // 引用块中的公式
          .katex-display {
            margin: 14px 0;
            background: rgba(255, 255, 255, 0.6);
            border-radius: 6px;
          }

          // 引用块中的行内公式
          .katex:not(.katex-display) {
            background: rgba(255, 255, 255, 0.5);
          }

          // 引用块中的强调文本
          strong {
            color: @primary-color;
            font-weight: 600;
          }

          // 引用块中的箭头符号
          code {
            background: rgba(255, 255, 255, 0.6);
            color: @primary-color;
            font-weight: 500;
          }
        }
        
        // 链接
        /deep/ a {
          color: @primary-color;
          text-decoration: none;
          border-bottom: 1px solid transparent;
          transition: all 0.3s;
          
          &:hover {
            color: #40a9ff;
            border-bottom-color: #40a9ff;
          }
        }
        
        // 表格
        /deep/ table {
          width: 100%;
          margin: 16px 0;
          border-collapse: collapse;
          border: 1px solid rgba(0, 0, 0, 0.1);
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
          
          th, td {
            border: 1px solid rgba(0, 0, 0, 0.08);
            padding: 10px 14px;
            text-align: left;
          }
          
          th {
            background: linear-gradient(180deg, rgba(24, 144, 255, 0.1) 0%, rgba(24, 144, 255, 0.05) 100%);
            font-weight: 600;
            color: @text-main;
          }
          
          tr:nth-child(even) {
            background-color: rgba(0, 0, 0, 0.02);
          }
          
          tr:hover {
            background-color: rgba(24, 144, 255, 0.05);
          }
        }
        
        // 分割线
        /deep/ hr {
          height: 1px;
          border: none;
          background: linear-gradient(to right, transparent, rgba(24, 144, 255, 0.3), transparent);
          margin: 24px 0;
          position: relative;

          &::after {
            content: '';
            position: absolute;
            top: -1px;
            left: 50%;
            transform: translateX(-50%);
            width: 60px;
            height: 3px;
            background: linear-gradient(90deg, transparent, @primary-color, transparent);
            border-radius: 2px;
          }
        }
        
        // 行内代码
        /deep/ code {
          background: rgba(24, 144, 255, 0.12);
          padding: 3px 6px;
          border-radius: 4px;
          font-family: 'Fira Code', 'Consolas', 'Monaco', 'Courier New', monospace;
          font-size: 0.9em;
          color: #e01e5a;
          border: 1px solid rgba(24, 144, 255, 0.2);
        }
        
        // 代码块
        /deep/ pre {
          background: linear-gradient(135deg, #f5f7fa 0%, #e8e8e8 100%);
          color: @text-main;
          padding: 16px;
          border-radius: 8px;
          overflow-x: auto;
          margin: 12px 0;
          border: 1px solid rgba(0, 0, 0, 0.1);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
          position: relative;
          
          &::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 3px;
            background: linear-gradient(90deg, @primary-color, #40a9ff, @primary-color);
            border-radius: 8px 8px 0 0;
          }
          
          code {
            background: transparent;
            padding: 0;
            color: inherit;
            border: none;
            font-size: 0.9em;
            white-space: pre;
          }
        }
        
        // KaTeX 公式样式 - 优化
        /deep/ .katex {
          font-size: 1.08em;
          color: @text-main;
        }

        // 行内公式（段落中、列表中、引用块中）
        /deep/ p .katex:not(.katex-display),
        /deep/ li .katex:not(.katex-display),
        /deep/ blockquote .katex:not(.katex-display) {
          background: rgba(24, 144, 255, 0.1);
          padding: 3px 8px;
          border-radius: 5px;
          border: 1px solid rgba(24, 144, 255, 0.2);
          margin: 0 3px;
          display: inline-block;
          vertical-align: middle;
        }

        // 独立的行内公式（不在其他元素中）
        /deep/ .katex:not(.katex-display) {
          background: rgba(24, 144, 255, 0.1);
          padding: 3px 8px;
          border-radius: 5px;
          border: 1px solid rgba(24, 144, 255, 0.2);
          margin: 0 3px;
          display: inline-block;
          vertical-align: middle;
        }

        // 块级公式（独立显示的公式）
        /deep/ .katex-display {
          margin: 24px 0;
          padding: 20px 24px;
          background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 248, 255, 0.95) 100%);
          border-radius: 10px;
          border: 1px solid rgba(24, 144, 255, 0.25);
          box-shadow: 0 3px 12px rgba(24, 144, 255, 0.12);
          overflow-x: auto;
          position: relative;

          &::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 4px;
            background: linear-gradient(90deg, @primary-color, #40a9ff, #69c0ff, @primary-color);
            border-radius: 10px 10px 0 0;
          }

          .katex {
            font-size: 1.2em;
            display: block;
            text-align: center;
            padding-top: 8px;
          }

          // 滚动条样式
          &::-webkit-scrollbar {
            height: 6px;
          }

          &::-webkit-scrollbar-thumb {
            background: rgba(24, 144, 255, 0.3);
            border-radius: 3px;

            &:hover {
              background: rgba(24, 144, 255, 0.5);
            }
          }
        }

        // 公式中的文本样式
        /deep/ .katex .text {
          font-family: 'PingFang SC', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }

        // 公式中的数字和运算符
        /deep/ .katex .mord, /deep/ .katex .mbin, /deep/ .katex .mrel {
          color: @text-main;
        }
        
        // 标记文本
        /deep/ mark {
          background: linear-gradient(180deg, transparent 60%, rgba(255, 235, 59, 0.4) 60%);
          padding: 0 2px;
          border-radius: 2px;
        }
        
        // 删除线
        /deep/ del {
          color: @text-secondary;
          text-decoration: line-through;
        }
        
        // 术语定义
        /deep/ dfn {
          font-style: italic;
          color: @primary-color;
          border-bottom: 1px dotted @primary-color;
          cursor: help;
        }
      }
    }
  }
}

// Typing Indicator
.typing-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 120px;
  
  .typing-dots {
    display: flex;
    gap: 4px;
    
    span {
      width: 6px;
      height: 6px;
      background: @primary-color;
      border-radius: 50%;
      animation: bounce 1.4s infinite ease-in-out both;
      
      &:nth-child(1) { animation-delay: -0.32s; }
      &:nth-child(2) { animation-delay: -0.16s; }
    }
  }
  
  .typing-text {
    font-size: 12px;
    color: @text-secondary;
  }
}

// Input Area
.chat-input-area {
  padding: 24px 28px;
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(20px);
  border-top: 1px solid rgba(255, 255, 255, 0.8);
  
  .input-wrapper {
    position: relative;
    background: #fff;
    border-radius: 20px;
    box-shadow: 0 8px 30px -6px rgba(0, 0, 0, 0.05);
    border: 1px solid rgba(0, 0, 0, 0.03);
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    align-items: flex-end;
    padding: 12px;
    
    &:focus-within {
      border-color: rgba(59, 130, 246, 0.3);
      box-shadow: 0 12px 40px -10px rgba(59, 130, 246, 0.15);
      transform: translateY(-2px);
    }
    
    .chat-textarea {
      flex: 1;
      border: none !important;
      box-shadow: none !important;
      background: transparent !important;
      padding: 8px 12px;
      resize: none;
      font-size: 15px;
      line-height: 1.6;
      color: @text-main;
      
      &::placeholder {
        color: #94a3b8;
      }
      
      &:focus {
        box-shadow: none;
      }
    }
    
    .input-actions {
      padding: 0 8px 8px 0;
      display: flex;
      gap: 12px;
      align-items: center;
      
      .image-btn, .voice-btn {
        width: 36px;
        height: 36px;
        border-radius: 12px;
        background: transparent;
        border: none;
        color: #64748b;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s;
        
        &:hover:not(:disabled) {
          background: rgba(0, 0, 0, 0.04);
          color: @primary-color;
          transform: scale(1.05);
        }
        
        &.uploading, &.recording {
          color: @primary-color;
          background: rgba(59, 130, 246, 0.1);
        }

        &.recording {
          color: #ef4444;
          background: rgba(239, 68, 68, 0.1);
          animation: pulse-recording 1.5s infinite;
        }
        
        &.disabled {
          color: #cbd5e1;
          cursor: not-allowed;
          opacity: 0.6;
          position: relative;
          
          &::after {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(255, 255, 255, 0.5);
            border-radius: 12px;
          }
        }
        
        &:disabled {
          color: #cbd5e1;
          cursor: not-allowed;
        }
        
        .anticon {
          font-size: 18px;
        }
      }
      
      .send-btn {
        width: 40px;
        height: 40px;
        border-radius: 14px;
        background: @primary-gradient;
        border: none;
        color: #fff;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
        box-shadow: 0 4px 12px rgba(37, 99, 235, 0.2);
        
        &:hover:not(:disabled) {
          transform: scale(1.05) rotate(-5deg);
          box-shadow: 0 8px 20px rgba(37, 99, 235, 0.3);
        }
        
        &:active:not(:disabled) {
          transform: scale(0.95);
        }
        
        &:disabled {
          background: #e2e8f0;
          color: #94a3b8;
          box-shadow: none;
          cursor: not-allowed;
        }
        
        .anticon {
          font-size: 18px;
          font-weight: bold;
        }
      }
    }
  }
  
  .input-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
    font-size: 12px;
    color: #909399;
    padding: 0 4px;
    
    .recording-tip {
      color: #ff4d4f;
      display: flex;
      align-items: center;
      gap: 4px;
      animation: pulse-text 1.5s infinite;
    }
    
    .speech-text-preview {
      color: @primary-color;
      display: flex;
      align-items: center;
      gap: 4px;
      max-width: 60%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    
    .error-tip {
      color: #ff4d4f;
    }
    
    .selected-image-preview {
      position: relative;
      display: inline-block;
      margin-right: 8px;
      
      img {
        width: 60px;
        height: 60px;
        object-fit: cover;
        border-radius: 8px;
        border: 1px solid rgba(0, 0, 0, 0.1);
        cursor: pointer;
        transition: transform 0.2s;
        
        &:hover {
          transform: scale(1.05);
        }
      }
      
      .remove-image-btn {
        position: absolute;
        top: -8px;
        right: -8px;
        font-size: 18px;
        color: #ff4d4f;
        background: #fff;
        border-radius: 50%;
        cursor: pointer;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        transition: transform 0.2s;
        
        &:hover {
          transform: scale(1.2);
        }
      }
      
      .uploading-icon {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        font-size: 18px;
        color: @primary-color;
      }
    }
  }
}

// 消息中的图片样式
.message-image {
  margin-bottom: 12px;
  
  img {
    max-width: 100%;
    max-height: 300px;
    border-radius: 8px;
    cursor: pointer;
    transition: transform 0.2s;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    
    &:hover {
      transform: scale(1.02);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
  }
}

// 图片预览弹窗样式
/deep/ .image-preview-modal {
  z-index: 10004 !important;
  
  .ant-modal-wrap {
    z-index: 10004 !important;
  }
  
  .ant-modal-mask {
    z-index: 10003 !important;
  }
  
  .ant-modal {
    z-index: 10004 !important;
  }
}

.image-preview-content {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
  
  img {
    max-width: 100%;
    max-height: 70vh;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  }
}

// Animations
@keyframes slideUp {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes pulse-ring {
  0% { transform: scale(0.8); opacity: 0.5; }
  100% { transform: scale(1.5); opacity: 0; }
}

@keyframes pulse {
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.5); opacity: 0; }
  100% { transform: scale(1); opacity: 0; }
}

@keyframes bounce {
  0%, 80%, 100% { transform: scale(0); }
  40% { transform: scale(1); }
}

@keyframes pulse-recording {
  0%, 100% { 
    box-shadow: 0 0 0 0 rgba(255, 77, 79, 0.7);
  }
  50% { 
    box-shadow: 0 0 0 8px rgba(255, 77, 79, 0);
  }
}

@keyframes pulse-text {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
}

@keyframes pulse-uploading {
  0%, 100% { 
    box-shadow: 0 0 0 0 rgba(24, 144, 255, 0.4);
  }
  50% { 
    box-shadow: 0 0 0 6px rgba(24, 144, 255, 0);
  }
}

// 错误提示弹窗样式
// 确保显示在 AgentChat 弹窗（z-index: 10002）的前面
/deep/ .speech-error-modal {
  z-index: 10003 !important;
  
  .ant-modal-wrap {
    z-index: 10003 !important;
  }
  
  .ant-modal-mask {
    z-index: 10002 !important; // 比 AgentChat 弹窗高，但比错误弹窗低
  }
  
  .ant-modal {
    z-index: 10003 !important;
  }
}

.error-modal-content {
  display: flex;
  align-items: flex-start;
  padding: 8px 0;
  
  .error-text {
    flex: 1;
    white-space: pre-line; // 支持换行显示
  }
}
</style>

